import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import api from '@/services/api'

export const useDataStore = defineStore('data', () => {
// State
const ascents = ref([])
const activities = ref([])
const achievements = ref([])
const challenges = ref([])
const loading = ref(false)
const error = ref(null)  // Getters
  const totalAscents = computed(() => ascents.value.length)

  const uniqueLocations = computed(() => {
    const locations = new Set(ascents.value.map(ascent => ascent.location_id))
    return locations.size
  })

  const highestPeak = computed(() => {
    if (ascents.value.length === 0) return 0
    return Math.max(...ascents.value.map(ascent => ascent.location_elevation || 0))
  })

  // Actions
  const loadAscents = async (force = false) => {
    if (!force && ascents.value.length > 0) return ascents.value // Return cached data

    try {
      loading.value = true
      error.value = null

      const response = await api.get('/api/ascents')
      ascents.value = response.data.locations || []

      console.log('Loaded ascents:', ascents.value.length)
      return ascents.value
    } catch (err) {
      console.error('Error loading ascents:', err)
      error.value = 'Failed to load ascents data'
      throw err
    } finally {
      loading.value = false
    }
  }

  const loadActivities = async (force = false) => {
    if (!force && activities.value.length > 0) return activities.value // Return cached data

    try {
      loading.value = true
      error.value = null

      const response = await api.get('/api/activities')
      activities.value = response.data.activities || []

      console.log('Loaded activities:', activities.value.length)
      return activities.value
    } catch (err) {
      console.error('Error loading activities:', err)
      error.value = 'Failed to load activities data'
      throw err
    } finally {
      loading.value = false
    }
  }

  const loadAchievements = async (force = false) => {
    if (!force && achievements.value.length > 0) return achievements.value // Return cached data

    try {
      loading.value = true
      error.value = null

      const response = await api.get('/api/achievment')
      achievements.value = response.data.achievments || []

      console.log('Loaded achievements:', achievements.value.length)
      return achievements.value
    } catch (err) {
      console.error('Error loading achievements:', err)
      error.value = 'Failed to load achievements data'
      throw err
    } finally {
      loading.value = false
    }
  }

  const loadChallenges = async (force = false) => {
    if (!force && challenges.value.length > 0) return challenges.value // Return cached data

    try {
      loading.value = true
      error.value = null

      // Get challenges from achievment endpoint
      const response = await api.get('/api/achievment')
      const achievments = response.data.achievments || []

      // Transform achievments into challenges format
      challenges.value = achievments.map((achievment, index) => {
        const totalLocations = achievment.locations.length
        const completedLocations = achievment.locations.filter(loc => loc.ascent_count > 0).length
        const progress = totalLocations > 0 ? Math.round((completedLocations / totalLocations) * 100) : 0
        const status = progress === 100 ? 'completed' : progress > 0 ? 'in-progress' : 'not-started'
        const statusText = status === 'completed' ? 'Completed' : status === 'in-progress' ? 'In Progress' : 'Not Started'

        return {
          id: achievment.id || index + 1,
          title: achievment.name,
          description: achievment.description,
          icon: '🏔️', // Default mountain icon
          category: 'peaks', // Default category
          status: status,
          statusText: statusText,
          target: totalLocations,
          current: completedLocations,
          progress: progress,
          rewards: ['Achievement Badge', `${progress} XP Points`],
          locations: achievment.locations // Include locations for details view
        }
      })

      console.log('Loaded challenges from achievments:', challenges.value.length)
      return challenges.value
    } catch (err) {
      console.error('Error loading challenges:', err)
      error.value = 'Failed to load challenges data'
      throw err
    } finally {
      loading.value = false
    }
  }

  const loadAllData = async () => {
    try {
      loading.value = true
      error.value = null

      // Load all data in parallel
      const [ascentsData, activitiesData, achievementsData, challengesData] = await Promise.allSettled([
        loadAscents(),
        loadActivities(),
        loadAchievements(),
        loadChallenges()
      ])

      // Log results
      if (ascentsData.status === 'fulfilled') {
        console.log('Ascents loaded successfully')
      } else {
        console.error('Failed to load ascents:', ascentsData.reason)
      }

      if (activitiesData.status === 'fulfilled') {
        console.log('Activities loaded successfully')
      } else {
        console.error('Failed to load activities:', activitiesData.reason)
      }

      if (achievementsData.status === 'fulfilled') {
        console.log('Achievements loaded successfully')
      } else {
        console.error('Failed to load achievements:', achievementsData.reason)
      }

      if (challengesData.status === 'fulfilled') {
        console.log('Challenges loaded successfully')
      } else {
        console.error('Failed to load challenges:', challengesData.reason)
      }

    } catch (err) {
      console.error('Error loading data:', err)
      error.value = 'Failed to load application data'
    } finally {
      loading.value = false
    }
  }

  const clearData = () => {
    ascents.value = []
    activities.value = []
    achievements.value = []
    challenges.value = []
    error.value = null
  }

  return {
// State
ascents,
activities,
achievements,
challenges,
loading,
error,    // Getters
    totalAscents,
    uniqueLocations,
    highestPeak,

    // Actions
    loadAscents,
    loadActivities,
    loadAchievements,
    loadChallenges,
    loadAllData,
    clearData
  }
})